import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr, fmtDate, fmtDateLong, fmtTimeRange, checkoutOriginUrl, goToCheckout } from "../api";
import { isNative } from "../mobileAuth";
import { useAuth } from "../AuthContext";
import { Page, SectionTitle, Logo } from "../components/Layout";
import { MyBookings } from "../components/MyBookings";
import { MyFlashDays } from "./Flash";
import { UserPlus, Zap, Monitor, Gift, LogOut, CalendarCheck, KeyRound, AlertTriangle } from "lucide-react";

function InvitaPremi() {
  const nav = useNavigate();
  return (
    <div className="card p-5 mb-4" data-testid="invita-premi-card">
      <div className="flex items-center gap-2 mb-2"><Gift size={18} color="#7C1FD6" /><span className="font-semibold">Invita e ricevi premi</span></div>
      <p className="text-sm text-gray-700">Invita un conoscente e ricevi un <b>Buono Amazon</b>: 100 € per la Tricopigmentazione, 20 € per il Coworking.</p>
      <p className="text-xs text-gray-400 mt-2">Il premio matura quando la persona invitata completa il trattamento o una giornata coworking pagata.</p>
      <button data-testid="go-invita" onClick={() => nav("/invita")} className="btn-primary w-full mt-3">Invita e ricevi premi</button>
    </div>
  );
}

function MyPackages() {
  const [pkgs, setPkgs] = useState([]);
  useEffect(() => { api.get("/my/packages").then((r) => setPkgs(r.data)).catch(() => {}); }, []);
  if (!pkgs.length) return null;
  const fmt = (s) => (s ? fmtDate(s) : "—");
  return (
    <div className="card p-5 mb-4" data-testid="my-packages">
      <div className="font-semibold mb-2">I tuoi pacchetti</div>
      <div className="space-y-3">
        {pkgs.map((p) => (
          <div key={p.id} className="border border-gray-100 rounded-xl p-3" data-testid={`pkg-${p.id}`}>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">{p.label}</span>
              <span className="text-sm font-bold text-violet-700">{p.remaining} {p.remaining === 1 ? "giornata residua" : "giornate residue"}</span>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Acquisto: {fmt(p.purchase_date)}{p.expiry_date ? ` · Scadenza: ${fmt(p.expiry_date)}` : " · Nessuna scadenza"}
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-400 mt-3">Le giornate acquistate possono essere utilizzate entro il periodo di validità del pacchetto. Qualora non fosse disponibile una postazione nelle date richieste, il termine di validità potrà essere prorogato per consentire l'utilizzo delle giornate residue.</p>
    </div>
  );
}

function MyResident() {
  const nav = useNavigate();
  const [subs, setSubs] = useState([]);
  const [renewBusy, setRenewBusy] = useState(false);
  useEffect(() => { api.get("/my/resident").then((r) => setSubs(r.data)).catch(() => {}); }, []);
  if (!subs.length) return null;
  const fmt = (s) => (s ? fmtDate(s) : "—");
  const reserved = subs.filter((s) => s.type === "RESIDENT_RESERVED");
  const flex = subs.filter((s) => s.type === "RESIDENT_FLEX");
  const statusLabel = (s) => (s.expired ? "Scaduto" : s.expiring_soon ? "In scadenza" : s.is_current ? "Attivo" : "Programmato");
  const statusCls = (s) => (s.expired ? "bg-gray-100 text-gray-600" : s.expiring_soon ? "bg-amber-100 text-amber-700" : s.is_current ? "bg-emerald-100 text-emerald-700" : "bg-violet-100 text-violet-700");
  const renew = async () => {
    setRenewBusy(true);
    try {
      const { data } = await api.post("/resident/checkout", { plan_id: "resident_reserved", origin_url: checkoutOriginUrl(), native: isNative() });
      if (data && data.checkout_url) { goToCheckout(data.checkout_url); }
      else { toast.error("Rinnovo non disponibile"); setRenewBusy(false); }
    } catch (e) { toast.error(apiErr(e)); setRenewBusy(false); }
  };
  return (
    <>
      {reserved.map((s) => (
        <div key={s.id} className="card p-5 mb-4 border-violet-200 bg-violet-50/40" data-testid={`reserved-card-${s.id}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="serif text-lg">Resident Reserved</div>
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${statusCls(s)}`} data-testid="reserved-status">{statusLabel(s)}</span>
          </div>
          <div className="text-sm text-gray-700 mt-1">Open Space riservata · postazione garantita ogni giorno del periodo.</div>
          <div className="text-xs text-gray-500 mt-2" data-testid="reserved-period">Periodo: {fmt(s.start_date)} → {fmt(s.last_usable_date)}</div>
          {s.is_current && <div className="text-xs text-gray-500 mt-0.5" data-testid="reserved-days-left">Giorni rimanenti: <b>{s.days_remaining}</b></div>}
          <div className="text-[11px] text-gray-600 mt-1">La Cabina Privacy è inclusa senza sovrapprezzo quando disponibile, ma non è garantita.</div>
          {s.expiring_soon && (
            <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="reserved-renew-banner">
              Il tuo periodo scade tra {s.days_remaining} {s.days_remaining === 1 ? "giorno" : "giorni"} — Rinnova e mantieni la tua Open Space garantita.
            </div>
          )}
          {s.is_current && (
            <div className="grid grid-cols-2 gap-2 mt-3">
              <button data-testid="reserved-book-os" onClick={() => nav("/prenota")} className="btn-primary text-sm py-2">Prenota la tua Open Space</button>
              <button data-testid="reserved-book-cabina" onClick={() => nav("/prenota")} className="btn-outline text-sm py-2">Prenota Cabina Privacy</button>
            </div>
          )}
          <button data-testid="reserved-renew" disabled={renewBusy} onClick={renew} className="btn-dark w-full text-sm py-2 mt-2">{renewBusy ? "Preparazione…" : "Rinnova ora"}</button>
        </div>
      ))}
      {flex.length > 0 && (
        <div className="card p-5 mb-4" data-testid="my-resident">
          <div className="font-semibold mb-2">Resident Flex</div>
          <div className="space-y-3">
            {flex.map((s) => (
              <div key={s.id} className="border border-gray-100 rounded-xl p-3" data-testid={`resident-${s.id}`}>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">{s.is_current ? "Attivo" : s.expired ? "Scaduto" : "Programmato"}</span>
                  <span className="text-sm font-bold text-violet-700">{s.credits_remaining} {s.credits_remaining === 1 ? "giornata residua" : "giornate residue"}</span>
                </div>
                <div className="text-xs text-gray-500 mt-1">Periodo: {fmt(s.start_date)} → {fmt(s.last_usable_date)} · {s.credits_total} totali · {s.credits_used} utilizzate</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function MyCourses() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { api.get("/my/courses").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoaded(true)); }, []);
  const STLAB = {
    paid: ["Iscrizione confermata", "text-green-700"],
    pending: ["In attesa di pagamento", "text-amber-600"],
    refunded: ["Iscrizione annullata · Rimborso effettuato", "text-gray-500"],
  };
  return (
    <div className="card p-5 mb-4" data-testid="my-courses">
      <div className="font-semibold mb-2">I corsi ai quali mi sono iscritto</div>
      {loaded && items.length === 0 && <p className="text-sm text-gray-500" data-testid="my-courses-empty">Non sei ancora iscritto a nessun corso.</p>}
      <div className="space-y-3">
        {items.map((e) => {
          const st = STLAB[e.status] || [e.status, "text-gray-500"];
          return (
            <div key={e.id} className="border border-gray-100 rounded-xl p-3" data-testid={`enr-${e.id}`}>
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{e.course?.title || e.course_title}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {e.course?.category ? `${e.course.category} · ` : ""}{fmtDateLong(e.course?.date)}{e.course?.time ? ` · ${fmtTimeRange(e.course.time)}` : ""}
                  </div>
                  {e.course?.teacher && <div className="text-xs text-gray-500">Docente: {e.course.teacher}</div>}
                </div>
                <span className={`text-[11px] font-semibold shrink-0 text-right ${st[1]}`}>{st[0]}</span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Importo: {e.amount > 0 ? `${e.amount} €` : "Gratuito"}
                {e.status === "refunded" && e.refund_amount ? ` · Rimborsato ${e.refund_amount} €` : ""}
              </div>
              {e.course?.id && <button data-testid={`enr-see-${e.id}`} onClick={() => nav(`/corsi/${e.course.id}`)} className="btn-outline text-xs py-1.5 px-3 mt-2">Vedi corso</button>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const ActionCard = ({ icon: Icon, label, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick} className="card p-4 text-left flex items-center gap-3">
    <Icon size={20} color="#7C1FD6" strokeWidth={1.6} /><span className="text-sm font-medium">{label}</span>
  </button>
);

function GoAcademyCard() {
  const nav = useNavigate();
  const [has, setHas] = useState(false);
  useEffect(() => { api.get("/academy/my/courses").then((r) => setHas((r.data || []).length > 0)).catch(() => {}); }, []);
  if (!has) return null;
  return (
    <div className="card p-4 mb-4" data-testid="go-allievo-card">
      <button data-testid="go-allievo" onClick={() => nav("/allievo")} className="btn-dark w-full">Vai all'Area Allievo</button>
    </div>
  );
}

function ChangePassword() {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password.length < 8) return toast.error("La nuova password deve avere almeno 8 caratteri");
    if (f.new_password !== f.confirm) return toast.error("Le password non coincidono");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      toast.success("Password aggiornata con successo");
      setF({ current_password: "", new_password: "", confirm: "" }); setOpen(false);
    } catch (er) { toast.error(apiErr(er)); }
    finally { setBusy(false); }
  };
  const inp = "w-full border border-gray-200 rounded-xl px-4 py-3 mt-1 focus:border-violet-500 outline-none";
  return (
    <div className="card p-5 mt-6">
      <button data-testid="open-change-password" onClick={() => setOpen(!open)} className="flex items-center justify-center gap-2 w-full font-semibold">
        <KeyRound size={18} color="#7C1FD6" /> Cambia password
      </button>
      {open && (
        <form onSubmit={submit} className="space-y-3 mt-4">
          <input data-testid="cp-current" type="password" required placeholder="Password attuale" className={inp} value={f.current_password} onChange={set("current_password")} />
          <input data-testid="cp-new" type="password" required minLength={8} placeholder="Nuova password (min 8 caratteri)" className={inp} value={f.new_password} onChange={set("new_password")} />
          <input data-testid="cp-confirm" type="password" required placeholder="Conferma nuova password" className={inp} value={f.confirm} onChange={set("confirm")} />
          <button data-testid="cp-submit" disabled={busy} className="btn-primary w-full">{busy ? "Aggiornamento…" : "Aggiorna password"}</button>
        </form>
      )}
    </div>
  );
}

function ArtistDash({ user }) {
  const nav = useNavigate();
  const [bal, setBal] = useState({ open_space: 0, cabina: 0 });
  const [bookings, setBookings] = useState([]);
  const lastFetch = useRef(0);
  const load = useCallback(() => {
    api.get("/my/balance").then((r) => setBal(r.data)).catch(() => {});
    api.get("/my/bookings").then((r) => setBookings(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const onResume = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastFetch.current < 1500) return; // dedupe stesso evento (focus+visibilitychange)
      lastFetch.current = now;
      load();
    };
    document.addEventListener("visibilitychange", onResume);
    window.addEventListener("focus", onResume);
    return () => {
      document.removeEventListener("visibilitychange", onResume);
      window.removeEventListener("focus", onResume);
    };
  }, [load]);
  const approved = user.approved && user.status !== "suspended";
  const hasDay = bookings.some((b) => ["confirmed", "completed"].includes(b.status));
  const next = bookings.filter((b) => b.status === "confirmed" && !b.is_past).sort((a, b) => a.date.localeCompare(b.date))[0];
  const today = new Date().toISOString().slice(0, 10);
  const todayBooking = bookings.find((b) => b.date === today && ["confirmed", "completed"].includes(b.status));

  const toggleAvail = async () => {
    try { const { data } = await api.post(`/bookings/${todayBooking.id}/available-today`); toast.success(data.available_today ? "Sei visibile come disponibile oggi" : "Disattivato"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <>
      <SectionTitle>Ciao {user.name.split(" ")[0]}</SectionTitle>
      {!approved && (
        <div className="card p-4 mb-4 bg-amber-50 border-amber-200" data-testid="pending-banner">
          <div className="font-semibold text-amber-800">{user.status === "suspended" ? "Profilo sospeso" : "Profilo in approvazione"}</div>
          <div className="text-sm text-amber-700 mt-1">{user.status === "suspended" ? "Contatta MAS per riattivare il tuo profilo." : "MAS sta verificando il tuo profilo. Appena approvato potrai prenotare."}</div>
        </div>
      )}

      <div className="card p-5 mb-4">
        <div className="text-xs uppercase tracking-wider text-gray-500">Prossima giornata</div>
        {next ? <div className="serif text-xl mt-1">{fmtDate(next.date)} · {next.station_type === "cabina" ? "Cabina Privacy" : "Open Space"}</div>
          : <div className="text-sm text-gray-400 mt-1">Nessuna giornata prenotata</div>}
        <button data-testid="dash-prenota" disabled={!approved} onClick={() => nav("/prenota")}
          className={`w-full mt-3 ${approved ? "btn-primary" : "btn-outline opacity-60 cursor-not-allowed"}`}>{next ? "Prenota un'altra giornata" : "Prenota una postazione"}</button>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="card p-4"><div className="text-3xl font-bold text-violet-700" data-testid="bal-open-space">{bal.open_space}</div><div className="text-xs text-gray-500 mt-1">Giornate Open Space disponibili</div></div>
        <div className="card p-4"><div className="text-3xl font-bold text-violet-700" data-testid="bal-cabina">{bal.cabina}</div><div className="text-xs text-gray-500 mt-1">Giornate Cabina disponibili</div></div>
      </div>

      <MyBookings onChange={load} />
      <MyFlashDays />
      <MyResident />
      <MyPackages />
      <MyCourses />
      <InvitaPremi />

      {hasDay && (
        <div className="grid grid-cols-2 gap-3 mb-4">
          <ActionCard icon={Zap} label="Crea il tuo Flash Day" testid="dash-flash" onClick={() => nav("/flash/new")} />
          <ActionCard icon={Monitor} label="Il tuo messaggio in vetrina" testid="dash-led" onClick={() => nav("/flash/led")} />
          {todayBooking && (
            <button data-testid="toggle-available-today" onClick={toggleAvail} className="card p-4 text-left col-span-2 flex items-center gap-2">
              <CalendarCheck size={18} color="#7C1FD6" /><span className="text-sm font-medium">Sono disponibile oggi</span>
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 mb-4">
        <ActionCard icon={Gift} label="I tuoi benefit" testid="dash-benefit" onClick={() => toast.info("Nessun benefit attivo")} />
        <ActionCard icon={UserPlus} label="Completa profilo" testid="dash-profile" onClick={() => nav("/profilo/completa")} />
      </div>
    </>
  );
}

function CustomerDash({ user }) {
  const [following, setFollowing] = useState([]);
  const [code, setCode] = useState(user.referral_code || "");
  useEffect(() => {
    api.get("/my/following").then((r) => setFollowing(r.data)).catch(() => {});
    api.get("/my/share").then((r) => setCode(r.data.referral_code)).catch(() => {});
  }, []);
  const nav = useNavigate();
  return (
    <>
      <SectionTitle>Ciao {user.name.split(" ")[0]}</SectionTitle>
      <InvitaPremi />
      <MyCourses />
      <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 mb-2">Artisti seguiti</h3>
      {following.length === 0 ? <p className="text-sm text-gray-400 mb-4">Non segui ancora nessun artista.</p> : (
        <div className="space-y-2 mb-4">{following.map((a) => <div key={a.id} className="card p-3 flex justify-between"><span className="font-medium">{a.artist_name}</span><span className="text-xs text-gray-500">{a.profession}</span></div>)}</div>
      )}
      <button data-testid="cust-trico" onClick={() => nav("/tricopigmentazione")} className="btn-outline w-full mb-2">Tricopigmentazione</button>
      <button data-testid="cust-corsi" onClick={() => nav("/corsi")} className="btn-outline w-full">Prossimi eventi</button>
    </>
  );
}

function AccountDeletion({ logout }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const canSubmit = confirm.trim().toUpperCase() === "ELIMINA";

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const { data } = await api.post("/account/delete-request", {});
      const req = new Date(data.deletion_requested_at);
      const anon = new Date(req.getTime() + 14 * 24 * 3600 * 1000);
      const anonStr = `${String(anon.getDate()).padStart(2, "0")}/${String(anon.getMonth() + 1).padStart(2, "0")}/${anon.getFullYear()}`;
      await logout();
      nav(`/cancellazione-account-richiesta?anon=${anonStr}`, { replace: true });
    } catch (er) {
      if (er?.response?.status === 409) {
        toast.error("Non puoi ancora richiedere la cancellazione perché risultano attività o crediti ancora aperti sul tuo account. Completa o gestisci prima gli impegni presenti; se hai bisogno di assistenza contatta MAS.");
      } else {
        toast.error(apiErr(er));
      }
      setBusy(false);
    }
  };

  return (
    <div className="mt-8 border-t border-gray-100 pt-6" data-testid="account-management">
      <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Gestione account</div>
      <button data-testid="delete-account-open" onClick={() => { setConfirm(""); setOpen(true); }}
        className="text-sm text-red-600 border border-red-200 rounded-xl px-4 py-2.5 hover:bg-red-50 transition-colors">
        Elimina account
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] z-[70] overflow-y-auto"
          data-testid="delete-account-modal" onClick={() => !busy && setOpen(false)}>
          <div className="bg-white rounded-2xl p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] w-full max-w-md max-h-[85dvh] overflow-y-auto overflow-x-hidden my-auto"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3"><AlertTriangle className="text-red-600" size={20} /><h3 className="serif text-xl">Elimina account</h3></div>
            <ul className="text-sm text-gray-700 space-y-2 list-disc pl-5">
              <li>L'account verrà <b>sospeso subito</b> e sarai disconnesso da tutti i dispositivi.</li>
              <li>Hai <b>14 giorni</b> per ripensarci: durante questo periodo non potrai usare l'account.</li>
              <li>Ti invieremo un'<b>email con un link sicuro</b> per annullare la richiesta.</li>
              <li>Dopo 14 giorni, se non annulli, i tuoi <b>dati personali verranno eliminati in modo definitivo</b>.</li>
              <li>Alcuni dati (pagamenti, documenti) possono essere <b>conservati</b> quando richiesto per obblighi di legge.</li>
              <li><b>Non</b> vengono effettuati rimborsi automatici.</li>
              <li>Prenotazioni, Resident o crediti attivi vanno <b>gestiti prima</b>.</li>
            </ul>
            <label className="block text-sm mt-4">Per confermare, digita <b>ELIMINA</b>
              <input data-testid="delete-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                autoCapitalize="characters" autoCorrect="off" spellCheck={false}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 mt-1 focus:border-red-400 outline-none" placeholder="ELIMINA" />
            </label>
            <div className="flex gap-2 mt-4">
              <button data-testid="delete-cancel" onClick={() => setOpen(false)} disabled={busy} className="btn-outline flex-1 text-sm">Annulla</button>
              <button data-testid="delete-confirm-submit" onClick={submit} disabled={busy || !canSubmit}
                className="flex-1 text-sm bg-red-600 text-white font-semibold rounded-xl px-4 py-2.5 disabled:opacity-40 hover:bg-red-700 transition-colors">
                {busy ? "Invio…" : "Elimina definitivamente"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProfiloPage() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  if (loading) return <Page><div className="text-center text-gray-400 pt-20">Caricamento…</div></Page>;
  if (!user) return (
    <Page>
      <div className="text-center pt-16"><Logo className="inline-block" sub /></div>
      <p className="text-center text-gray-600 mt-6 mb-4">Accedi o registrati per il tuo profilo.</p>
      <button data-testid="profilo-login" onClick={() => nav("/accedi")} className="btn-primary w-full mb-3">Accedi</button>
      <button data-testid="profilo-register" onClick={() => nav("/registrati?role=artist")} className="btn-outline w-full">Registrati gratis</button>
    </Page>
  );
  return (
    <Page>
      {user.role === "admin" && <div className="card p-4 mb-4"><button onClick={() => nav("/admin")} className="btn-dark w-full">Vai all'Area Admin</button></div>}
      {(user.roles || []).includes("teacher") && <div className="card p-4 mb-4" data-testid="go-docente-card"><button data-testid="go-docente" onClick={() => nav("/docente")} className="btn-dark w-full">Vai all'Area Docente</button></div>}
      <GoAcademyCard />
      {user.role === "customer" ? <CustomerDash user={user} /> : user.role === "artist" ? <ArtistDash user={user} /> : <SectionTitle>Ciao {user.name}</SectionTitle>}
      <ChangePassword />
      {user.role !== "admin" && <AccountDeletion logout={logout} />}
      <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/"); }} className="mt-8 text-sm text-gray-500 flex items-center gap-2 mx-auto"><LogOut size={15} /> Esci</button>
    </Page>
  );
}
